/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import { Injectable } from "@angular/core";
import { CONTEXTS_INFO, RegistryContexts } from "@app/models/constants/registry-constants";
import { CommonActions } from "@app/store/common/common.actions";
import { CommonState } from "@app/store/common/common.state";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { SaluteHelperService } from "@trec/api";
import { DATE_FMT_8601, TrecTranslation } from "@trec/api/common";
import { TrecApiError, TrecError } from "@trec/api/errors";
import {
  ReasonerOnDemandPrescription,
  ReasonerService,
  ReasonerWarningPrescription,
} from "@trec/api/reasoner";
import { Feature, Key2, RegistryService, UserEntry2 } from "@trec/api/registry";
import { ErrorAction, StoreAction } from "@trec/components/store";
import { format, subDays } from "date-fns";
import { forkJoin, of, throwError } from "rxjs";
import { catchError, finalize, map, switchMap, switchMapTo, tap } from "rxjs/operators";
import { Goal } from "../models/goal";
import { Microlearning } from "../models/microlearning";
import { PersonalExperiment } from "../models/personal-experiments";
import { TpAdvActions } from "./patient-treatment-plan-adv.actions";

/**
 * Ngxs treatment plan state.
 */
export interface PatientTreatmentPlanAdvStateModel {
  /**
   * List of available app features for current patient.
   */
  availableAppFeatures: (Feature & TrecTranslation)[] | undefined;
  /**
   * List of available app features grouped by context.
   */
  availableAppFeaturesByContext?: Record<string, (Feature & TrecTranslation)[]>;
  /**
   * List of available personal experiments.
   */
  availablePersonalExperiments: PersonalExperiment[] | undefined;
  /**
   * List of goals.
   */
  availableGoals: Goal[] | undefined;
  /**
   * List of available on demand modules.
   */
  availableOnDemandDialogs: TrecTranslation[] | undefined;
  /**
   * List of available microlearning modules.
   */
  availableMicrolearning: Microlearning[] | undefined;

  /**
   * On demand prescription.
   */
  onDemandPrescription: ReasonerOnDemandPrescription | null;
  /**
   * Patient's warnings prescriptions.
   */
  warningsPatientPrescriptions: ReasonerWarningPrescription[] | null;

  /**
   * lista di esami
   */
  availableEsamiPdta: any[] | undefined;

  /**
   * Flag indicating that operation has failed.
   */
  error: TrecError | TrecApiError | null;
}

@State<PatientTreatmentPlanAdvStateModel>({
  name: "tretamentPlanAdv",
  defaults: {
    availableAppFeatures: undefined,
    availableAppFeaturesByContext: undefined,
    availablePersonalExperiments: undefined,
    availableGoals: undefined,
    availableOnDemandDialogs: undefined,
    availableMicrolearning: undefined,
    availableEsamiPdta: undefined,

    onDemandPrescription: null,
    warningsPatientPrescriptions: null,

    error: null,
  },
})
@Injectable()
export class PatientTreatmentPlanAdvState {
  constructor(
    private store: Store,
    private registry: RegistryService,
    private reasoner: ReasonerService,
    private saluteHelperService: SaluteHelperService,
  ) {}

  // Selectors ////////////////////////////////////////////////////////////////////////////////////

  // Available lists ////////////////////////////

  @Selector() static availableAppFeatures(state: PatientTreatmentPlanAdvStateModel) {
    return state.availableAppFeatures;
  }

  @Selector() static availableAppFeaturesByContext(state: PatientTreatmentPlanAdvStateModel) {
    return state.availableAppFeaturesByContext;
  }
  @Selector() static availablePersonalExperiments(state: PatientTreatmentPlanAdvStateModel) {
    return state.availablePersonalExperiments;
  }
  @Selector() static availableGoals(state: PatientTreatmentPlanAdvStateModel) {
    return state.availableGoals;
  }
  @Selector() static availableOnDemandDialogs(state: PatientTreatmentPlanAdvStateModel) {
    return state.availableOnDemandDialogs;
  }
  @Selector() static availableMicrolearning(state: PatientTreatmentPlanAdvStateModel) {
    return state.availableMicrolearning;
  }
  @Selector() static availableEsamiPdta(state: PatientTreatmentPlanAdvStateModel) {
    return state.availableEsamiPdta;
  }

  // Data ///////////////////////////////////////

  @Selector() static onDemandPrescription(state: PatientTreatmentPlanAdvStateModel) {
    return state.onDemandPrescription;
  }
  @Selector() static warningsPatientPrescriptions(state: PatientTreatmentPlanAdvStateModel) {
    return state.warningsPatientPrescriptions;
  }

  // Select status //////////////////////////////

  @Selector() static error(state: PatientTreatmentPlanAdvStateModel) {
    return state.error;
  }

  // Actions //////////////////////////////////////////////////////////////////////////////////////

  @Action([
    TpAdvActions.FetchAppFeatures,
    TpAdvActions.UpdateAppFeatures,
    TpAdvActions.FetchPersonalExperiments,
    TpAdvActions.UpdatePersonalExperiments,
    TpAdvActions.FetchGoals,
    TpAdvActions.UpdateGoals,
    TpAdvActions.FetchOnDemandDialogs,
    TpAdvActions.UpdateOnDemandDialogs,
    TpAdvActions.FetchMicrolearning,
    TpAdvActions.UpdateMicrolearning,
    TpAdvActions.FetchWarningsPatient,
    TpAdvActions.UpdateWarningsPatient,
    TpAdvActions.FetchEsamiPdtaList,
  ])
  fetching(ctx: StateContext<PatientTreatmentPlanAdvStateModel>, _action: StoreAction) {
    // Set loading flag to true
    ctx.patchState({ error: null });
  }

  @Action(TpAdvActions.TpAdvError)
  errors(ctx: StateContext<PatientTreatmentPlanAdvStateModel>, { payload }: ErrorAction) {
    // Save error in store
    ctx.patchState({ error: payload });
  }

  // App features /////////////////////////////////////////////////////////////////////////////////

  @Action(TpAdvActions.FetchAppFeatures)
  fetchAppFeatures(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    _action: TpAdvActions.FetchAppFeatures,
  ) {
    // App features data are already present in user entry of current patient in CommonState
    // -> So, load only available app features
    let contextInfo = CONTEXTS_INFO;
    if (Object.keys(contextInfo).length === 0) {
      contextInfo = JSON.parse(localStorage.getItem("CONTEXTS_INFO") || "{}");
    }

    // Select current patient id and context
    return this.store.selectOnce(CommonState.patientUserEntryByContext).pipe(
      switchMap((ues) => {
        return this.registry.getFeaturesTranslations().pipe(
          map((translations) => {
            let availableAppFeatures: (Feature & TrecTranslation)[] = [];
            const featuresByContext: Record<string, (Feature & TrecTranslation)[]> = {};
            ues?.forEach((ue) => {
              if (!featuresByContext[ue.contextId]) {
                featuresByContext[ue.contextId] = [];
              }

              let availableAppFeaturesTemp = ue.context.features.map((f: any) => ({
                // Merge features and translations
                ...f,
                ...(Object.values(translations).find((t: any) => t.id === f._id) || {
                  id: f._id,
                  it: f._id,
                  en: f._id,
                }),
                contextLabel: contextInfo[ue.contextId]?.label,
                context: ue.contextId,
              }));
              availableAppFeatures = availableAppFeatures?.concat(
                availableAppFeaturesTemp.filter(
                  ({ id }) => !availableAppFeatures?.find((f) => f.id == id),
                ),
              );
              featuresByContext[ue.contextId].push(...availableAppFeaturesTemp);
            });

            return ctx.patchState({
              availableAppFeaturesByContext: featuresByContext,
              error: null,
            });
          }),
        );
      }),
      catchError((err) => ctx.dispatch(new TpAdvActions.TpAdvError(err))),
    );
  }

  @Action(TpAdvActions.UpdateAppFeatures)
  updateAppFeatures(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    { payload: { appFeatures } }: TpAdvActions.UpdateAppFeatures,
  ) {
    const contesti = this.store.selectSnapshot(CommonState.patientContext);

    if (!contesti) {
      throw new Error(`Missing contesti of a patient.`);
    }

    const structureId = this.store.selectSnapshot(CommonState.currentStructureId);

    if (!structureId) {
      throw new Error(`Try to enabled devices but structureId has been not set`);
    }

    // Select current patient id
    return this.store.selectOnce(CommonState.patientUserEntryByContext).pipe(
      map((ue) => {
        // Create new updated user entry

        if (!ue) {
          throw new Error(`Missing user entry of a patient. Cannot update app features`);
        }

        // Update features
        let newEntry: UserEntry2[] = [];
        // If feature exisits -> add it to userEntry
        ue.forEach((ue) => {
          const appFeaturesAdd = appFeatures
            .filter(
              (selection) => selection.selected && ue.contextId === (selection.item as any).context,
            )
            .map((s) => new Key2({ name: s.item._id, type: "FEATURE", value: s.item._id }));
          // If feature does not exisits -> remove it from userEntry
          const appFeaturesRemove = appFeatures
            .filter(
              (selection) =>
                !selection.selected && ue.contextId === (selection.item as any).context,
            )
            .map((s) => s.item._id);
          const userEntry = ue.addKey(appFeaturesAdd).removeKey(appFeaturesRemove);
          userEntry.structure = structureId;
          newEntry?.push(userEntry);
        });

        return newEntry;
      }),
      // Update userEntry on server
      switchMap((userEntry) =>
        this.registry.updateUserEntryMulti(userEntry).pipe(
          switchMap((resUe) => {
            // Toggle loading
            ctx.patchState({ error: null });
            // Update user entry in store
            return ctx.dispatch(
              new CommonActions.UpdatePatientData({
                patientId: resUe[0].patient_id,
                changes: { userEntry: resUe },
              }),
            );
          }),
        ),
      ), // !! Call returns 204
      // Any error -> throw
      catchError((err) =>
        ctx.dispatch(new TpAdvActions.TpAdvError(err)).pipe(switchMapTo(throwError(err))),
      ),
    );
  }

  // Personal Experiments /////////////////////////////////////////////////////////////////////////

  @Action(TpAdvActions.FetchPersonalExperiments)
  fetchPersonalExperiments(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    _action: TpAdvActions.FetchPersonalExperiments,
  ) {
    return this.registry.getTranslations(["personal_expertiment"]).pipe(
      map((translations) =>
        translations.map<PersonalExperiment>((t) => ({
          id: t.id,
          label: t.it,
          startDate: new Date(),
        })),
      ),
      tap((list) => {
        ctx.patchState({ availablePersonalExperiments: list, error: null });
      }),
      catchError((err) => ctx.dispatch(new TpAdvActions.TpAdvError(err))),
    );
  }

  @Action(TpAdvActions.UpdatePersonalExperiments)
  updatePersonalExperiments(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    { payload: { selectedPes } }: TpAdvActions.UpdatePersonalExperiments,
  ) {
    const contesti = this.store.selectSnapshot(CommonState.patientContext);

    if (!contesti) {
      throw new Error(`Missing contesti of a patient.`);
    }

    const structureId = this.store.selectSnapshot(CommonState.currentStructureId);

    if (!structureId) {
      throw new Error(`Try to enabled devices but structureId has been not set`);
    }

    // Select app prescription
    return this.store.selectOnce(CommonState.patientUserEntryByContext).pipe(
      map((ue) => {
        // Create new updated user entry

        if (!ue) {
          throw new Error(`Missing user entry of a patient. Cannot update pe`);
        }

        // Prepare user entry keys of goals
        const keys = selectedPes.map((pe) => {
          const k = new Key2({ name: pe.id, type: "PERSONAL_EXPERIMENTS", value: {} });
          k.start_date = pe.startDate;
          return k;
        });

        let newEntry: UserEntry2[] = [];

        ue.forEach((ue) => {
          if (contesti.indexOf(ue.contextId as RegistryContexts) >= 0) {
            const userEntry = ue
              .removeKeysOfType("PERSONAL_EXPERIMENTS")
              // Add current new config
              .addKey(keys);
            userEntry.structure = structureId;
            newEntry?.push(userEntry);
          }
        });

        return newEntry;
      }),
      // Update userEntry on server
      switchMap((userEntry) =>
        this.registry.updateUserEntryMulti(userEntry).pipe(
          switchMap((userEntry2) => {
            // !! Call returns 204
            // Toggle loading
            ctx.patchState({ error: null });
            // Update user entry in store
            return ctx.dispatch(
              new CommonActions.UpdatePatientData({
                patientId: userEntry[0].patient_id,
                changes: { userEntry: userEntry2 },
              }),
            );
          }),
        ),
      ),
      // Any error -> throw
      catchError((err) =>
        ctx.dispatch(new TpAdvActions.TpAdvError(err)).pipe(switchMapTo(throwError(err))),
      ),
    );
  }

  // Goals ////////////////////////////////////////////////////////////////////////////////////////

  @Action(TpAdvActions.FetchGoals)
  fetchGoals(ctx: StateContext<PatientTreatmentPlanAdvStateModel>, {}: TpAdvActions.FetchGoals) {
    return this.registry.getTranslations(["goal"]).pipe(
      // Use current date as default start date
      map((translations) =>
        translations.map<Goal>((t) => ({ id: t.id, label: t.it, startDate: new Date() })),
      ),
      tap((list) => {
        ctx.patchState({ availableGoals: list, error: null });
      }),
      catchError((err) => ctx.dispatch(new TpAdvActions.TpAdvError(err))),
    );
  }

  @Action(TpAdvActions.UpdateGoals)
  updateGoals(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    { payload: { selectedGoals } }: TpAdvActions.UpdateGoals,
  ) {
    const structureId = this.store.selectSnapshot(CommonState.currentStructureId);

    if (!structureId) {
      throw new Error(`Try to enabled devices but structureId has been not set`);
    }
    // Select app prescription
    return this.store.selectOnce(CommonState.patientUserEntryByContext).pipe(
      map((ues) => {
        // Create new updated user entry

        if (!ues) {
          throw new Error(`Missing user entry of a patient. Cannot update goals`);
        }

        // Prepare user entry keys of goals
        const goalsKeys = selectedGoals.map((g) => {
          const k = new Key2({ name: g.id, type: "GOAL", value: {} });
          k.start_date = g.startDate;
          return k;
        });

        let newEntry: UserEntry2[] = [];

        ues.forEach((ue: UserEntry2) => {
          const newUe = ue
            // Remove old goals config
            .removeKeysOfType("GOAL")
            // Add current new config
            .addKey(goalsKeys);
          newUe.structure = structureId;
          newEntry.push(newUe);
        });
        return newEntry;
      }),
      // Update userEntry on server
      switchMap((userEntry) =>
        this.registry.updateUserEntryMulti(userEntry).pipe(
          switchMap(() => {
            // !! Call returns 204
            // Toggle loading
            ctx.patchState({ error: null });
            // Update user entry in store
            return ctx.dispatch(
              new CommonActions.UpdatePatientData({
                patientId: userEntry[0].patient_id,
                changes: { userEntry: userEntry },
              }),
            );
          }),
        ),
      ),
      // Any error -> throw
      catchError((err) =>
        ctx.dispatch(new TpAdvActions.TpAdvError(err)).pipe(switchMapTo(throwError(err))),
      ),
    );
  }

  // OnDemand dialogs /////////////////////////////////////////////////////////////////////////////

  @Action(TpAdvActions.FetchOnDemandDialogs)
  fetchOnDemandDialogs(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    _action: TpAdvActions.FetchOnDemandDialogs,
  ) {
    return CommonState.selectOncePatientId(this.store).pipe(
      switchMap((patientId) =>
        forkJoin([
          this.reasoner.getAvailableOnDemandDialogs(),
          this.reasoner.getOndemandDialogPrescriptions(patientId || "", undefined, true),
        ]),
      ),
      tap(([availableList, prescriptions]) => {
        ctx.patchState({
          availableOnDemandDialogs: availableList,
          onDemandPrescription: prescriptions[0] || null,
          error: null,
        });
      }),
      catchError((err) => ctx.dispatch(new TpAdvActions.TpAdvError(err))),
    );
  }

  @Action(TpAdvActions.UpdateOnDemandDialogs)
  updateOnDemandDialogs(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    { payload: { prescription, isNew } }: TpAdvActions.UpdateOnDemandDialogs,
  ) {
    // Ensure start_date is greather than now
    if (new Date(prescription.startDate).valueOf() <= Date.now()) {
      prescription.startDate = format(new Date(), DATE_FMT_8601);
    }

    return (
      isNew
        ? this.reasoner.prescribeOnDemandPrescription(prescription)
        : this.reasoner.updateOnDemandPrescription(prescription)
    ).pipe(
      tap(() => {
        // On success -> update prescription in store
        ctx.patchState({ onDemandPrescription: prescription, error: null });
      }),
      catchError((err) =>
        ctx.dispatch(new TpAdvActions.TpAdvError(err)).pipe(switchMapTo(throwError(err))),
      ),
    );
  }

  // Warnings patient /////////////////////////////////////////////////////////////////////////////

  @Action(TpAdvActions.FetchWarningsPatient)
  fetchWarningsPatient(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    _action: TpAdvActions.FetchWarningsPatient,
  ) {
    return CommonState.selectOncePatientId(this.store).pipe(
      switchMap((patientId) => this.reasoner.getWarnings(patientId || "", true)),
      map((prescriptions) => prescriptions.filter((w) => w.warningType === "patient")),
      tap((prescriptions) => {
        ctx.patchState({ warningsPatientPrescriptions: prescriptions, error: null });
      }),
      catchError((err) => ctx.dispatch(new TpAdvActions.TpAdvError(err))),
    );
  }

  @Action(TpAdvActions.UpdateWarningsPatient)
  updateWarningsPatient(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    { payload: { insert, remove } }: TpAdvActions.UpdateWarningsPatient,
  ) {
    // Prescribe new warnings
    const createCalls =
      insert.length > 0
        ? insert.map((p) =>
            this.reasoner.prescribeWarning(p, localStorage.getItem("c3-curr-context")),
          )
        : [of(null)];
    // Update warnings to stop (update end date)
    const updateCalls =
      remove.length > 0
        ? remove
            // Set end date to yesterday to remove/stop the warning prescription
            .map((p) => ({ ...p, to: format(subDays(new Date(), 1), DATE_FMT_8601) }))
            .map((p) => this.reasoner.updateWarningPrescription(p))
        : [of(null)];

    return forkJoin([...createCalls, ...updateCalls]).pipe(
      switchMap(() => {
        // On success -> update prescription in store
        ctx.patchState({ error: null });
        return ctx.dispatch(new TpAdvActions.FetchWarningsPatient());
      }),
      catchError((err) => {
        // Reload prescriptions and throw error
        ctx.dispatch(new TpAdvActions.FetchWarningsPatient());
        return ctx.dispatch(new TpAdvActions.TpAdvError(err)).pipe(switchMapTo(throwError(err)));
      }),
    );
  }

  // Microlearning /////////////////////////////////////////////////////////////////////////

  @Action(TpAdvActions.FetchMicrolearning)
  fetchMicrolearning(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    {}: TpAdvActions.FetchMicrolearning,
  ) {
    return CommonState.selectOnceCurrentContext(this.store).pipe(
      switchMap((context) => this.registry.getTranslations(["microlearning", context])), // Filter by ctx
      map((translations) =>
        translations.map<Microlearning>((t) => ({ id: t.id, label: t.it, startDate: new Date() })),
      ),
      tap((list) => {
        ctx.patchState({ availableMicrolearning: list, error: null });
      }),
      catchError((err) => ctx.dispatch(new TpAdvActions.TpAdvError(err))),
    );
  }

  @Action(TpAdvActions.UpdateMicrolearning)
  updateMicrolearning(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    { payload: { selectedMls } }: TpAdvActions.UpdateMicrolearning,
  ) {
    const structureId = this.store.selectSnapshot(CommonState.currentStructureId);

    if (!structureId) {
      throw new Error(`Try to enabled devices but structureId has been not set`);
    }
    // Select app prescription
    return this.store.selectOnce(CommonState.patientUserEntryByContext).pipe(
      map((ues) => {
        // Create new updated user entry

        if (!ues) {
          throw new Error(`Missing user entry of a patient. Cannot update ml`);
        }

        // Prepare user entry keys of ml
        const keys = selectedMls.map((ml) => {
          const k = new Key2({ name: ml.id, type: "MICROLEARNING", value: {} });
          k.start_date = ml.startDate;
          return k;
        });

        let newEntry: UserEntry2[] = [];

        ues.forEach((ue: UserEntry2) => {
          const newUe = ue
            // Remove old config
            .removeKeysOfType("MICROLEARNING")
            // Add current new config
            .addKey(keys);
          newUe.structure = structureId;
          newEntry.push(newUe);
        });

        return newEntry;
      }),
      // Update userEntry on server
      switchMap((userEntry) =>
        this.registry.updateUserEntryMulti(userEntry).pipe(
          switchMap(() => {
            // !! Call returns 204
            // Toggle loading
            ctx.patchState({ error: null });
            // Update user entry in store
            return ctx.dispatch(
              new CommonActions.UpdatePatientData({
                patientId: userEntry[0].patientId,
                changes: { userEntry },
              }),
            );
          }),
        ),
      ),
      // Any error -> throw
      catchError((err) =>
        ctx.dispatch(new TpAdvActions.TpAdvError(err)).pipe(switchMapTo(throwError(err))),
      ),
    );
  }

  // Clear Status /////////////////////////////////////////////////////////////////////////////////

  @Action(TpAdvActions.ClearStatus)
  clearStatus(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    _action: TpAdvActions.ClearStatus,
  ) {
    ctx.patchState({
      availableAppFeatures: undefined,
      availableAppFeaturesByContext: undefined,
      availablePersonalExperiments: undefined,
      availableGoals: undefined,
      availableOnDemandDialogs: undefined,
      availableMicrolearning: undefined,
      availableEsamiPdta: undefined,

      onDemandPrescription: null,
      warningsPatientPrescriptions: null,

      error: null,
    });
  }

  //Pdta
  @Action(TpAdvActions.FetchEsamiPdtaList)
  fetchEsamiPdtaList(
    ctx: StateContext<PatientTreatmentPlanAdvStateModel>,
    { payload: { trecUserId, context } }: TpAdvActions.FetchEsamiPdtaList,
  ) {
    ctx.patchState({ error: null });

    return this.saluteHelperService.getListaTemplatePerContestoPdta(trecUserId, context).pipe(
      finalize(() => ctx.patchState({ error: null })),
      map((data) => {
        ctx.patchState({ availableEsamiPdta: data });
      }),
      catchError((err) =>
        ctx.dispatch(new TpAdvActions.TpAdvError(err)).pipe(switchMapTo(throwError(err))),
      ),
    );
  }
}
